import React, { useEffect, useRef, useState } from "react";
import { mixUrl, takeChannel } from "@/lib/audioPool";
import { motion } from "framer-motion";
import { Pencil, Sparkles, X, ArrowUp } from "lucide-react";
import { useGuidedTour } from "@/lib/guidedTour";
import TourLineEditor from "../tour/TourLineEditor";

const BED_VOLUME = 0.18;

const fade = (el, to, ms, done) => {
  if (!el) return;
  const from = el.volume;
  const t0 = performance.now();
  const step = (t) => {
    const p = Math.min(1, (t - t0) / ms);
    el.volume = Math.max(0, Math.min(1, from + (to - from) * p));
    if (p < 1) requestAnimationFrame(step); else done?.();
  };
  requestAnimationFrame(step);
};

// "Coming up" narrated hype that plays right after the intro, before the presenter hits Demo.
export default function HypeCard({ line, audioUrl, bedUrl, isAdmin, onLineSaved, onClose, persist = false, desktopOnly = false }) {
  const { isActive } = useGuidedTour();
  const [editing, setEditing] = useState(false);
  const voiceRef = useRef(null);
  const bedRef = useRef(null);

  useEffect(() => { if (isActive) onClose(); }, [isActive, onClose]);

  useEffect(() => {
    const vh = audioUrl ? takeChannel("voice", audioUrl) : null;
    const bh = bedUrl ? takeChannel("bed", mixUrl(bedUrl, BED_VOLUME), { loop: true, volume: 0 }) : null;
    const v = vh?.el || null;
    const b = bh?.el || null;
    voiceRef.current = v;
    bedRef.current = b;
    let closeTimer;
    if (b) {
      bh.play().then(() => fade(b, 1, 1200)).catch(() => {});
    }
    const t = setTimeout(() => vh?.play().catch(() => {}), 400);
    if (v) {
      v.onended = () => {
        fade(b, 0, 1800);
        if (!persist) closeTimer = setTimeout(onClose, 2200);
      };
    }
    return () => {
      clearTimeout(t);
      clearTimeout(closeTimer);
      [vh, bh].forEach((h) => h?.release());
    };
  }, [audioUrl, bedUrl, onClose, persist]);

  const startEdit = () => {
    voiceRef.current?.pause();
    if (voiceRef.current) voiceRef.current.onended = null;
    setEditing(true);
  };

  return (
    <motion.aside
      data-testid="demo-hype-card"
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 180, damping: 20 }}
      className={`${desktopOnly ? "hidden lg:block " : ""}fixed z-[90] bottom-6 right-6 w-[400px] max-w-[calc(100vw-32px)] rounded-2xl border border-emerald-400/40 bg-[rgba(12,19,34,0.96)] backdrop-blur-xl shadow-[0_0_50px_rgba(16,185,129,0.15),0_20px_60px_rgba(0,0,0,0.55)] p-5 text-slate-100`}
    >
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300 mb-2">
        <Sparkles className="w-3.5 h-3.5" /> Coming up
        <span className="ml-auto flex items-center gap-1">
          {isAdmin && !editing && (
            <button onClick={startEdit} data-testid="demo-hype-edit" aria-label="Edit hype line"
              className="p-1 rounded-md text-slate-400 hover:text-brand-cyan hover:bg-white/5 transition-colors">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onClose} data-testid="demo-hype-skip" aria-label="Skip"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
            Skip <X className="w-3 h-3" />
          </button>
        </span>
      </div>
      {editing ? (
        <TourLineEditor
          step="HY"
          text={line.text}
          edited={line.edited}
          maxLength={800}
          onCancel={() => setEditing(false)}
          onSaved={(l) => { onLineSaved(l); setEditing(false); }}
        />
      ) : (
        <p className="text-slate-200 text-sm leading-relaxed" data-testid="demo-hype-text">{line.text}</p>
      )}
      {!editing && (
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-400" data-testid="demo-hype-hint">
          <motion.span animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 1.2 }}
            className="inline-flex w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-400/40 items-center justify-center text-emerald-300">
            <ArrowUp className="w-3.5 h-3.5" />
          </motion.span>
          Press <span className="text-white font-semibold">Auto Demo</span> or <span className="text-white font-semibold">Manual Demo</span> above to begin
        </div>
      )}
    </motion.aside>
  );
}
